import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { useLogger } from '@/lib/logger';

/**
 * Stock Hold-Back data layer.
 *
 * Mirrors `useCostOfGoods`'s structure (query + mutations + invalidate on
 * success) but routes every user-facing string through `t()` — the cost-of-goods
 * hook hardcodes its toast copy, which is exactly the debt not to inherit given
 * zh parity is CI-enforced.
 */
export function useHoldBack() {
  const { t } = useTranslation('inventory');
  const logger = useLogger();
  const utils = trpc.useUtils();

  const { data, isLoading, isFetching, error, refetch } =
    trpc.inventory.getHoldBackRules.useQuery({});

  const saveHoldBackRules = trpc.inventory.saveHoldBackRules.useMutation({
    onSuccess: (result) => {
      void utils.inventory.getHoldBackRules.invalidate();
      toast({
        title: t('holdBack.savedTitle'),
        // The change applies on the NEXT push cycle, not immediately — markers
        // are stamped and the scheduled push picks them up. Saying so here is
        // what stops "the hold-back isn't working" reports within the hour.
        description: t('holdBack.savedDesc', {
          saved: result.savedCount,
          removed: result.deletedCount,
          marked: result.markedForResyncCount,
        }),
        duration: 10000,
      });
    },
    onError: (err) => {
      toast({
        title: t('holdBack.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('holdBack.unknownError'),
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (!error) return;
    logger.error('Failed to load stock hold-back rules', {
      error: error instanceof Error ? error.message : String(error),
    });
  }, [error, logger]);

  return {
    rules: data?.rules ?? [],
    isLoading,
    // Surfaced so the page can render a real error state. An empty grid and a
    // failed load must not look identical here: on this page "nothing is held
    // back" is a claim with money attached.
    loadError: error ?? null,
    // Retry belongs to the query, not to `window.location.reload()`: a reload
    // throws away every other cached surface (and any half-typed matrix) to
    // re-run one request.
    refetch,
    isFetching,
    saveHoldBackRules,
  };
}
